import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useT } from '../lib/i18n';
import { motion, useMotionValue, useTransform, animate } from 'motion/react';
import Icon from './Icon';
import { playNavTap, installNavSoundUnlock } from '../lib/navSound';

type Item = { to: string; labelKey: string; icon: string };

const ITEMS: Item[] = [
  { to: '/',        labelKey: 'nav.home',    icon: 'home' },
  { to: '/games',   labelKey: 'nav.games',   icon: 'sports_esports' },
  { to: '/wallet',  labelKey: 'nav.wallet',  icon: 'currency_rupee_circle' },
  { to: '/rewards', labelKey: 'nav.rewards', icon: 'card_giftcard' },
  { to: '/profile', labelKey: 'nav.profile', icon: 'person' },
];

// Phase R5+ Task E (2026-05-10) — 3D popped tab with REAL cutout.
//
// Final architecture (after several iterations):
//   - ONE useMotionValue (`x`), explicitly driven by motion's
//     `animate()` from an effect whenever `activeX` (the active
//     LI's measured centre) changes. The animation uses the
//     bouncy spring config — `animate()` is the most reliable
//     way to apply spring physics imperatively.
//   - The bubble is an OVERLAY (one element, not per-LI) that
//     reads `style={{ x }}` so its translate follows the spring.
//   - The cutout is a sibling motion.div whose mask-image
//     gradient stop reads from the SAME `x` via useTransform.
//     mask-image with a transparent radial-gradient circle =
//     REAL transparency through the nav strip.
//   - Single source of truth → bubble + cutout always lockstep,
//     no matter how bouncy.
//
// Trade-off: the active tab's icon is hidden in its LI (the
// bubble overlay shows it instead). That's the only structural
// change vs the layoutId-per-LI architecture; verification +
// alignment is straightforward.

const NAV_HEIGHT_PX = 52;
// Phase R5+ visual fix (2026-05-10): bubble shrunk 48 → 42 per user
// feedback — icons inside (28px Material Symbols, 32px avatar) stay
// the same size, the bubble just gets a touch tighter so the rim
// of the real cutout is visibly proud of the bubble's edge. With
// scale 1.15 the bubble's visual diameter is 48.3px, the cutout
// diameter is 64px, leaving ~8px of clear cutout rim around the
// bubble — enough to read as a recess the bubble nests in.
const BUBBLE_DIAMETER_PX = 42;
const NOTCH_RADIUS_PX = 32;
const BOUNCY_SPRING = { type: 'spring' as const, stiffness: 450, damping: 14, mass: 1 };

export default function BottomNav() {
  const { user } = useAuth();
  const t = useT();
  const loc = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const ulRef = useRef<HTMLUListElement>(null);
  const [activeX, setActiveX] = useState(-200);

  // Single shared motion value. Both bubble translate and mask
  // gradient-stop read from this. Driven by `animate()` whenever
  // activeX changes.
  const x = useMotionValue(activeX);

  const activeIx = ITEMS.findIndex((it) =>
    it.to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(it.to)
  );

  // Measure the active LI's centre x in nav-local coordinates.
  // ResizeObserver covers viewport changes; activeIx covers route changes.
  useEffect(() => {
    if (!user || !ulRef.current || !navRef.current) return;
    const measure = () => {
      const li = ulRef.current!.children[activeIx] as HTMLElement | undefined;
      if (!li) { setActiveX(-200); return; }
      const liRect = li.getBoundingClientRect();
      const navRect = navRef.current!.getBoundingClientRect();
      setActiveX(liRect.left - navRect.left + liRect.width / 2);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(navRef.current);
    ro.observe(ulRef.current);
    return () => ro.disconnect();
  }, [activeIx, user]);

  // Drive the motion value with the bouncy spring whenever
  // activeX changes. Returning the controls' stop() in the
  // cleanup cancels any in-flight animation if activeX moves
  // again before settling.
  useEffect(() => {
    const controls = animate(x, activeX, BOUNCY_SPRING);
    return () => controls.stop();
  }, [activeX, x]);

  // Install the audio-unlock listener on first mount so the user's
  // first interaction warms the AudioContext (browsers gate
  // playback behind a gesture). Idempotent — safe across remounts.
  useEffect(() => { installNavSoundUnlock(); }, []);

  // Play the chime whenever activeIx CHANGES — but skip the first
  // render so a fresh page load doesn't bing on its own. The ref
  // tracks the previous index; only emit the sound when it actually
  // moves.
  const prevActiveIxRef = useRef<number | null>(null);
  useEffect(() => {
    if (prevActiveIxRef.current === null) {
      // First render — just record, don't play.
      prevActiveIxRef.current = activeIx;
      return;
    }
    if (prevActiveIxRef.current !== activeIx) {
      prevActiveIxRef.current = activeIx;
      playNavTap();
    }
  }, [activeIx]);

  // Mask-image string derived from `x`. useTransform creates a
  // motion value that maps `x` → gradient string; passing it to
  // the style prop makes motion update the mask-image on every
  // animation frame.
  const maskImage = useTransform(
    x,
    (cx) => `radial-gradient(circle ${NOTCH_RADIUS_PX}px at ${cx}px 0px, transparent ${NOTCH_RADIUS_PX - 1}px, black ${NOTCH_RADIUS_PX}px)`
  );

  if (!user) return null;

  const activeItem = activeIx >= 0 ? ITEMS[activeIx] : null;
  const isAvatarActive = activeItem?.to === '/profile' && !!user.profile_image_url;

  return (
    <nav
      ref={navRef}
      className="fixed bottom-0 inset-x-0 z-40
                 pb-[env(safe-area-inset-bottom)]"
    >
      {/* ─── Real cutout + masked top border ────────────────────
          mask-image makes the cutout area transparent — page
          content behind the nav shows through. The `border-t` is
          applied HERE (on the masked element) so it gets cut by
          the same mask: the straight horizontal line has a clean
          ~64px gap exactly at the cutout. The user-visible "line
          breaking the illusion" is gone. */}
      <motion.div
        aria-hidden="true"
        style={{ maskImage, WebkitMaskImage: maskImage }}
        className="absolute inset-0
                   bg-white dark:bg-arena-800
                   backdrop-blur
                   border-t border-arena-200/60 dark:border-arena-600/60"
      />

      {/* ─── Curved arc following the cutout's bottom rim ───────
          A 64×32 box with rounded bottom corners (each corner
          radius = NOTCH_RADIUS so the two corners meet exactly
          at the bottom-centre, forming a full half-circle).
          With `border-bottom` only, the visible outline is just
          the curved underside — connecting the gap left by the
          masked border-t with a smooth half-circle dip.
          Driven by the SAME `x` motion value as the cutout, so
          arc + cutout + bubble all jiggle together. */}
      <motion.div
        aria-hidden="true"
        style={{
          x,
          marginLeft: -NOTCH_RADIUS_PX,
          width: NOTCH_RADIUS_PX * 2,
          height: NOTCH_RADIUS_PX,
          top: 0,
          borderBottomLeftRadius: `${NOTCH_RADIUS_PX}px ${NOTCH_RADIUS_PX}px`,
          borderBottomRightRadius: `${NOTCH_RADIUS_PX}px ${NOTCH_RADIUS_PX}px`,
        }}
        className="absolute left-0 z-20 pointer-events-none
                   border-b border-arena-200/60 dark:border-arena-600/60"
      />

      {/* ─── Tab list ────────────────────────────────────────────
          The active LI's icon is HIDDEN (visibility: hidden) —
          the bubble overlay below shows the icon instead, so we
          don't render two icons at the same x. */}
      <ul
        ref={ulRef}
        className="relative z-30 max-w-xl mx-auto flex justify-around items-end"
        style={{ height: NAV_HEIGHT_PX }}
      >
        {ITEMS.map((it, ix) => {
          const isActive = ix === activeIx;
          const isAvatarTab = it.to === '/profile' && !!user.profile_image_url;
          return (
            <li key={it.to} className="relative flex-1 h-full">
              <NavLink
                to={it.to}
                end={it.to === '/'}
                className="relative flex flex-col items-center justify-end h-full
                           outline-none"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {/* Flat icon — visible only when this tab is NOT active. */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-[10px]
                             flex items-center justify-center pointer-events-none"
                  style={{ visibility: isActive ? 'hidden' : 'visible' }}
                >
                  {isAvatarTab ? (
                    <img
                      src={user.profile_image_url!}
                      alt=""
                      className="w-[22px] h-[22px] rounded-full object-cover
                                 ring-1 ring-arena-300/70 dark:ring-arena-600/70"
                    />
                  ) : (
                    <Icon
                      name={it.icon}
                      size={22}
                      filled
                      weight={400}
                      className="text-arena-500 dark:text-neon-200/60"
                    />
                  )}
                </div>

                {/* Label — always visible; colour swaps on active. */}
                <span
                  className={`absolute bottom-1 left-1/2 -translate-x-1/2
                              text-[11px] leading-none transition-all
                              ${isActive
                                ? 'font-extrabold tracking-wide text-brand-600 dark:text-neon-300'
                                : 'font-medium text-arena-500 dark:text-neon-200/60'}`}
                >
                  {t(it.labelKey)}
                </span>
              </NavLink>
            </li>
          );
        })}
      </ul>

      {/* ─── Bubble overlay ──────────────────────────────────────
          ONE motion.div for the entire nav. style={{ x }} reads
          the SAME motion value the cutout's mask reads from →
          guaranteed lockstep on every frame.
          marginLeft = -BUBBLE_DIAMETER_PX/2 so the bubble's
          centre lands on x. */}
      {activeItem && (
        <motion.div
          style={{
            x,
            marginLeft: -(BUBBLE_DIAMETER_PX / 2),
            // top derived from the diameter so the bubble's CSS
            // centre lands on y = 0 (the cutout's vertical centre).
            // Was hard-coded `-top-6` for the 48px bubble; now
            // computed so any future diameter change auto-corrects.
            top: -(BUBBLE_DIAMETER_PX / 2),
            width: BUBBLE_DIAMETER_PX,
            height: BUBBLE_DIAMETER_PX,
          }}
          animate={{ scale: 1.15 }}
          transition={BOUNCY_SPRING}
          className="absolute left-0 z-50 rounded-full
                     flex items-center justify-center
                     bg-gradient-to-br from-brand-400 via-brand-500 to-neon-400
                     dark:from-brand-500 dark:via-brand-400 dark:to-neon-300
                     ring-2 ring-white/80 dark:ring-arena-900/80
                     drop-shadow-[0_4px_12px_rgba(var(--brand-rgb),0.45)]
                     dark:drop-shadow-[0_4px_14px_rgba(143,255,254,0.4)]
                     pointer-events-none"
        >
          {isAvatarActive ? (
            <img
              src={user.profile_image_url!}
              alt=""
              className="w-[32px] h-[32px] rounded-full object-cover
                         ring-2 ring-white/80 dark:ring-arena-900/80"
            />
          ) : (
            <Icon
              name={activeItem.icon}
              size={28}
              filled
              weight={600}
              className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]"
            />
          )}
        </motion.div>
      )}
    </nav>
  );
}
